import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Button, Card, Col, Row } from "react-bootstrap";

import CsLineIcons from "../../cs-line-icons";
import { send as pageInfo } from "../../actions/page";
import { withNavigation } from "../../utils/wrapper";
import SourceDataImportButton, { SourceDataImportWorkflow } from "./import-source-data";

const sections = [
    {
        title: "Firm",
        items: [
            {
                title: "Firm Information",
                detail: "Firm profile and identity",
                icon: "building",
                to: "/dashboard/settings/firm",
                permissions: ["VIEW.SETTINGS", "MANAGE.FIRM"]
            },
            {
                title: "Users",
                detail: "User access and accounts",
                icon: "user",
                to: "/dashboard/users",
                permissions: ["VIEW.USER"]
            },
            {
                title: "Roles",
                detail: "Role permissions",
                icon: "tag",
                to: "/dashboard/settings/roles",
                permissions: ["VIEW.SETTINGS"]
            }
        ]
    },
    {
        title: "Operations",
        items: [
            {
                title: "Accounting",
                detail: "Accounting defaults",
                icon: "dollar",
                to: "/dashboard/settings/accounting",
                permissions: ["VIEW.SETTINGS"]
            },
            {
                title: "Calendars",
                detail: "Calendar configuration",
                icon: "calendar",
                to: "/dashboard/settings/calendars",
                permissions: ["VIEW.SETTINGS"]
            },
            {
                title: "Email Profiles",
                detail: "Outbound email profiles",
                icon: "email",
                to: "/dashboard/settings/email",
                permissions: ["VIEW.SETTINGS"]
            },
            {
                title: "Events",
                detail: "Event automation",
                icon: "notification",
                to: "/dashboard/settings/events",
                permissions: ["VIEW.SETTINGS"]
            },
            {
                title: "Hooks",
                detail: "Hook destinations",
                icon: "link",
                to: "/dashboard/settings/hooks",
                permissions: ["VIEW.SETTINGS"]
            },
            {
                title: "Leads",
                detail: "Lead intake settings",
                icon: "presentation",
                to: "/dashboard/settings/leads",
                permissions: ["VIEW.SETTINGS"]
            },
            {
                title: "Boards",
                detail: "Board workflow settings",
                icon: "board-1",
                to: "/dashboard/settings/boards",
                permissions: ["VIEW.SETTINGS"]
            }
        ]
    },
    {
        title: "Configuration",
        items: [
            {
                title: "Expenses",
                detail: "Expense codes",
                icon: "tag",
                to: "/dashboard/settings/expenses",
                permissions: ["VIEW.SETTINGS"]
            },
            {
                title: "Rates",
                detail: "Rate tiers",
                icon: "tag",
                to: "/dashboard/settings/rates",
                permissions: ["VIEW.SETTINGS"]
            },
            {
                title: "Surveys",
                detail: "Survey configuration",
                icon: "clipboard",
                to: "/dashboard/settings/surveys",
                permissions: ["EDIT.SURVEY"]
            },
            {
                title: "Templates",
                detail: "Email and report templates",
                icon: "content",
                to: "/dashboard/templates",
                permissions: ["VIEW.SETTINGS"]
            }
        ]
    }
];

class Settings extends React.Component {
    state = {
        importing: false
    };

    componentDidMount = () => {
        this.props.pageInfo({ title: "Settings", breadcrumb: "Settings" });
    };

    get permissions() {
        return this.props.auth?.permissions || this.props.auth?.user?.permissions || [];
    }

    canView = (item) => {
        if (!item.permissions?.length) return true;
        return item.permissions.some((permission) => this.permissions.includes(permission));
    };

    open = (path) => {
        if (!path) return;
        this.props.navigate(path);
    };

    startImport = () => {
        this.setState({ importing: true });
        this.props.pageInfo({ title: "Import Source Data", breadcrumb: "Import Source Data" });
    };

    closeImport = () => {
        this.setState({ importing: false });
        this.props.pageInfo({ title: "Settings", breadcrumb: "Settings" });
    };

    renderItem = (item) => {
        if (!this.canView(item)) return null;

        return (
            <Col sm="6" xl="4" key={item.to}>
                <Card className="h-100">
                    <Card.Body className="d-flex flex-column">
                        <div className="d-flex align-items-start gap-3">
                            <div
                                className="d-flex align-items-center justify-content-center bg-light border"
                                style={{ width: 40, height: 40, borderRadius: 6, flex: "0 0 40px" }}
                            >
                                <CsLineIcons icon={item.icon} size="21" />
                            </div>
                            <div className="w-100">
                                <button
                                    type="button"
                                    className="btn btn-link p-0 fw-bold text-start"
                                    onClick={() => this.open(item.to)}
                                >
                                    {item.title}
                                </button>
                                <div className="small text-muted">{item.detail}</div>
                            </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-4">
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                onClick={() => this.open(item.to)}
                            >
                                <CsLineIcons icon="arrow-right" size="14" className="me-1" />
                                Open
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        );
    };

    renderSection = (section) => {
        const items = section.items.filter(this.canView);
        if (!items.length) return null;

        return (
            <Col xs="12" key={section.title}>
                <h2 className="small-title">{section.title}</h2>
                <Row className="g-3">
                    {items.map(this.renderItem)}
                </Row>
            </Col>
        );
    };

    render() {
        const hasItems = sections.some((section) => section.items.some(this.canView));
        const canImport = this.canView({ permissions: ["VIEW.SETTINGS", "MANAGE.FIRM"] });

        return (
            <>
                <Col md="8" className="float-right text-end">
                    {canImport ? (
                        <SourceDataImportButton onStart={this.startImport} disabled={this.state.importing} />
                    ) : null}
                </Col>
                {this.state.importing ? (
                    <SourceDataImportWorkflow show={this.state.importing} onHide={this.closeImport} />
                ) : (
                    <Col xs="12">
                        {hasItems ? (
                            <Row className="g-4">
                                {sections.map(this.renderSection)}
                            </Row>
                        ) : (
                            <Card>
                                <Card.Body className="text-muted">No settings are available for this user.</Card.Body>
                            </Card>
                        )}
                    </Col>
                )}
            </>
        );
    }
}

Settings.propTypes = {
    auth: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    pageInfo: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default withNavigation(connect(mapStateToProps, { pageInfo })(Settings));
